import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Job, jobMicroUsd } from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { LanguageCode, RelativeTime } from '../../task/TaskBadges';
import { formatMicroUsd } from '../formatCost';
import { jobDisplayTitle, jobIcon } from '../jobDisplay';
import { JobStatusPill } from '../JobBadges';
import { useGetSpendQuery, useListJobsInfiniteQuery } from '../jobEndpoints';

/** The characters a job's title or request is cut to in the list (QZ-D47). */
const TITLE_MAX = 80;

/**
 * The drafts the job left for review over every task it stored, auto-approved ones included
 * (QZ-D56, QZ-D161): "12/40". Counted when each chunk finished, so a draft approved by hand
 * later still counts as a draft; the chunks' `approved` counts only auto-approval.
 */
const draftsOfTasks = (job: Job): string => {
  const drafts = job.chunks.reduce((total, chunk) => total + chunk.drafted, 0);
  const approved = job.chunks.reduce((total, chunk) => total + chunk.approved, 0);
  return `${drafts}/${drafts + approved}`;
};

/** Whether a job is still being planned or generated, and so worth polling for. */
const isActive = (job: Job): boolean => job.status === 'planning' || job.status === 'running';

/**
 * The jobs list at `/jobs` (QZ-D20, QZ-D55): two spend totals, translation spend
 * (QZ-D55, QZ-D117) and feedback spend (QZ-D155, FBK-D2), the jobs newest first, and
 * "Load more". Polls every 10s while a listed job is still planning or running, following
 * `jobs.html` without its *Drafts to review* box.
 */
export const JobsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [pollingInterval, setPollingInterval] = useState(0);
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, error } =
    useListJobsInfiniteQuery(undefined, { pollingInterval, skipPollingIfUnfocused: true });
  const { data: spend, isLoading: spendLoading, error: spendError } = useGetSpendQuery();

  const jobs = useMemo(() => data?.pages.flatMap((page) => page.jobs) ?? [], [data]);

  useEffect(() => {
    setPollingInterval(jobs.some(isActive) ? 10_000 : 0);
  }, [jobs]);

  const code = problemCode(error);
  const open = (jobId: string) => navigate(`/jobs/${jobId}`);

  const spendValue = (value: number | undefined): string => {
    if (spendLoading) {
      return '…';
    }
    if (spendError || value === undefined) {
      return '—';
    }
    return formatMicroUsd(value);
  };

  const monthHint = (): string => {
    if (spendLoading) {
      return '…';
    }
    if (spendError || !spend) {
      return '—';
    }
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${spend.month}-01T00:00:00Z`));
  };

  return (
    <main className="qz-main">
      <div className="qz-container">
        <div className="qz-page-header">
          <div>
            <h1>{t('job.list.heading')}</h1>
            <p className="qz-sub">{t('job.list.sub')}</p>
          </div>
          <Link className="btn btn-primary" to="/jobs/new">
            <Icon name="wand-magic-sparkles" /> {t('job.list.newJob')}
          </Link>
        </div>

        <div className="qz-stats mb-4">
          <div className="qz-card qz-stat">
            <div className="qz-stat-label">{t('job.list.allTime')}</div>
            <div className="qz-stat-value">{spendValue(spend?.allTimeMicroUsd)}</div>
            <div className="qz-stat-hint">{t('job.list.costHint')}</div>
          </div>
          <div className="qz-card qz-stat">
            <div className="qz-stat-label">{t('job.list.thisMonth')}</div>
            <div className="qz-stat-value">{spendValue(spend?.thisMonthMicroUsd)}</div>
            <div className="qz-stat-hint">{monthHint()}</div>
          </div>
          <div className="qz-card qz-stat">
            <div className="qz-stat-label">{t('job.list.translation')}</div>
            <div className="qz-stat-value">{spendValue(spend?.translationAllTimeMicroUsd)}</div>
            <div className="qz-stat-hint">
              {t('job.list.thisMonth')}{' '}
              <span className="qz-mono">{spendValue(spend?.translationThisMonthMicroUsd)}</span>
            </div>
            <div className="qz-stat-hint">{t('job.list.translationHint')}</div>
          </div>
          <div className="qz-card qz-stat">
            <div className="qz-stat-label">{t('job.list.feedback')}</div>
            <div className="qz-stat-value">{spendValue(spend?.feedbackAllTimeMicroUsd)}</div>
            <div className="qz-stat-hint">
              {t('job.list.thisMonth')}{' '}
              <span className="qz-mono">{spendValue(spend?.feedbackThisMonthMicroUsd)}</span>
            </div>
            <div className="qz-stat-hint">{t('job.list.feedbackHint')}</div>
          </div>
        </div>

        <div className="qz-card">
          {isLoading && <p className="qz-card-body qz-muted mb-0">{t('job.list.loading')}</p>}
          {error && (
            <div className="qz-card-body">
              <div className="alert alert-danger mb-0">
                <Icon name="circle-exclamation" />
                <div>{t(code ? `error.${code}` : 'error.unknown')}</div>
              </div>
            </div>
          )}
          {!isLoading && !error && jobs.length === 0 && !hasNextPage && (
            <div className="qz-empty">
              <Icon name="wand-magic-sparkles" />
              <strong>{t('job.list.empty')}</strong>
              <span>{t('job.list.emptyHint')}</span>
              <div className="mt-3">
                <Link to="/jobs/new" className="btn btn-primary">
                  {t('job.list.createFirst')}
                </Link>
              </div>
            </div>
          )}
          {!isLoading && jobs.length > 0 && (
            <table className="table table-hover table-clickable align-middle qz-table-stack">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>{t('job.field.request')}</th>
                  <th>{t('job.field.language')}</th>
                  <th>{t('task.filter.status')}</th>
                  <th className="text-end">{t('job.field.draftsOfTasks')}</th>
                  <th className="text-end">{t('job.field.cost')}</th>
                  <th className="text-end">{t('job.field.created')}</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.jobId}
                    role="button"
                    tabIndex={0}
                    onClick={() => open(job.jobId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        open(job.jobId);
                      } else if (e.key === ' ') {
                        e.preventDefault();
                        open(job.jobId);
                      }
                    }}
                  >
                    <td>
                      <div className="fw-semibold">
                        <span className="qz-muted">
                          <Icon name={jobIcon(job)} />
                        </span>{' '}
                        {jobDisplayTitle(job, TITLE_MAX)}
                      </div>
                    </td>
                    <td>
                      <LanguageCode language={job.language} />
                    </td>
                    <td>
                      <JobStatusPill job={job} showProgress />
                    </td>
                    <td className="text-end" data-label={t('job.field.draftsOfTasks')}>
                      {draftsOfTasks(job)}
                    </td>
                    <td className="text-end qz-mono" data-label={t('job.field.cost')}>
                      {formatMicroUsd(jobMicroUsd(job))}
                    </td>
                    <td className="text-end qz-small qz-muted">
                      <RelativeTime iso={job.createdAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!isLoading && jobs.length > 0 && hasNextPage && (
            <div className="qz-card-footer">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={isFetching}
                onClick={() => fetchNextPage()}
              >
                {t('job.list.loadMore')}
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default JobsPage;
